import React, { ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Container from '../container/Container';
import {
  Outlet,
  useNavigate,
  useLocation,
  createSearchParams,
} from 'react-router-dom';
import { useAppDispatch } from '../../store/store';
import {
  createSession,
  fetchAuthToken,
  getAuthToken,
  getSessionId,
  getSessionLoading,
  getClientId,
  getD2dApiResponse,
  fetchD2dInfo,
} from '../../store/sessionSlice';
import { getAppError } from '../../store/appStateSlice';
import { executeLogout } from '../../store/logoutSlice';
import {
  AUTH_TOKEN_SESSION_STORAGE_KEY,
  SESSION_ID_SESSION_STORAGE_KEY,
  D2D_TOKEN_SESSION_STORAGE_KEY,
  CLIENT_ID_SESSION_STORAGE_KEY,
} from '../../utils/constants/sessionStorageConstants';
import {
  getRedirectRouteOnError,
  isEmpty,
  isMock,
  isRouteBehindLogin,
} from '../../utils/helpers';
import { useToShowTimeoutPopup } from '../../utils/customHooks/useToShowTimeoutPopup';
import Header from '../header/Header';
import Footer from '../footer/Footer';
import SessionTimeout from '../sessionTimeout/SessionTimeout';
import SynchronyLoader from '../loader/SynchronyLoader';
import CancelModal from '../customerVerification/CancelModal';

const AppLayout = (): ReactElement => {
  const [sessionStartTime, setSessionStartTime] = useState(
    new Date().getTime(),
  );
  const [showPopup, setShowPopup] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const errors: any = useSelector(getAppError);
  const authToken = useSelector(getAuthToken);
  const isLoading = useSelector(getSessionLoading);
  const sessionId = useSelector(getSessionId);

  const [showTimeOutPrompt, setShowTimeOutPrompt]: any =
    useToShowTimeoutPopup();

  const d2dToken = sessionStorage.getItem(D2D_TOKEN_SESSION_STORAGE_KEY);
  const clientId =
    sessionStorage.getItem(CLIENT_ID_SESSION_STORAGE_KEY) ||
    useSelector(getClientId);
  const d2dApiResponse = useSelector(getD2dApiResponse);
  if (authToken) {
    sessionStorage.setItem(AUTH_TOKEN_SESSION_STORAGE_KEY, authToken);
  }

  if (sessionId) {
    sessionStorage.setItem(SESSION_ID_SESSION_STORAGE_KEY, sessionId);
  }

  const pathName = location.pathname;
  const isSessionTimeoutAllowed =
    pathName === '/welcome' || pathName === '/dashboard';
  const isWhiteBgColorNeeded = pathName === '/welcome' || pathName === '/';

  const navigateWithParams = () => {
    const currentParams = new URLSearchParams(window.location.search);
    navigate({
      pathname: '/welcome',
      search: `?${createSearchParams(currentParams)}`,
    });
  };

  const handleD2dResponse = () => {
    if (d2dApiResponse?.strategyInfo?.promoCode && clientId) {
      // when d2d request returns success, pass the payload to session
      dispatch(createSession(d2dApiResponse));
    } else {
      navigate('/link-not-valid');
    }
  };

  const handleD2dToken = (token: string) => {
    // handle if d2d request has been made or not
    if (isEmpty(d2dApiResponse)) {
      dispatch(fetchD2dInfo(token));
    } else if (!isEmpty(d2dApiResponse)) {
      handleD2dResponse();
    }
  };

  useEffect(() => {
    sessionStorage.removeItem(AUTH_TOKEN_SESSION_STORAGE_KEY);
    dispatch(fetchAuthToken());
  }, [dispatch]);

  useEffect(() => {
    if (!showTimeOutPrompt) {
      setSessionStartTime(new Date().getTime());
    }
  }, [showTimeOutPrompt]);

  useEffect(() => {
    // handle if authToken present or not
    if (authToken) {
      if (d2dToken) {
        // start d2d flow
        handleD2dToken(d2dToken);
      } else if (clientId) {
        // start regular session flow, without d2d
        dispatch(createSession({}));
      }
    } else if (!authToken && !clientId) {
      navigate('/link-not-valid');
    }
  }, [authToken, clientId, d2dToken, d2dApiResponse]);

  useEffect(() => {
    /*
     * Making sure the user starts at the top of the page
     * on every route (pathName) change
     */
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [pathName]);

  useEffect(() => {
    if (!isMock()) {
      if (Object.keys(errors).length > 0) {
        // to check if any errors have been found before navigating to the appropriate landing page
        const [firstKey] = Object.keys(errors); // gets the first error inside the object
        const error: any = errors[firstKey]; // extracting first object
        //firstKey is the name of api i.e. account, session, refresh store in redux
        if (error.navigateToErrorPage) {
          const redirectLink = getRedirectRouteOnError(
            firstKey,
            error?.status,
            // error?.code // this parameter can be used to get the businessErrors[0].code from redux
            // error.code is already saved in redux
          ); // passing status from first object
          navigate(redirectLink);
        }
      } else if (sessionId) {
        navigateWithParams();
      }
    }
  }, [errors, sessionId]);

  const handleBackButton = (event: any) => {
    if (isRouteBehindLogin()) {
      setShowPopup(true);
      event.preventDefault();
    } else if (!isSessionTimeoutAllowed) {
      event.preventDefault();
      navigate('/link-not-valid');
    }
  };

  const handleKeypress = (event: any) => {
    if (isRouteBehindLogin()) {
      //Check if the route is behind login page
      //When the route is behind login, check if the reload shortcut is used
      //If used stop the refresh and show the cancel modal
      if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
        setShowPopup(true);
        event.preventDefault();
        event.returnValue = '';
      }
    }
  };

  const handleBeforeUnload = (event: any) => {
    if (isRouteBehindLogin()) {
      // when the refresh icon or F5 key is used, the refresh is stopped
      event.preventDefault();
      event.returnValue = '';
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeypress);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('keydown', handleKeypress);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    window.history.pushState(null, '');
    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [handleBackButton]);

  const handleConfirmNavigation = () => {
    setShowPopup(false);
    dispatch(executeLogout());
    navigate('/thank-you');
  };

  const handleCancelNavigation = () => {
    setShowPopup(false);
  };

  const handlePopupShowHide = (show: boolean) => {
    setShowTimeOutPrompt(show);
  };

  return (
    <main>
      <Container bgClass="bg-header-footer header-container">
        <Header />
      </Container>
      {isLoading && <SynchronyLoader />}
      {showTimeOutPrompt && (
        <SessionTimeout
          handlePopupShowHide={handlePopupShowHide}
          sessionStartTime={sessionStartTime}
        />
      )}
      {showPopup && (
        <CancelModal
          onClose={handleCancelNavigation}
          onConfirm={handleConfirmNavigation}
        />
      )}
      <Container
        bgClass={
          isWhiteBgColorNeeded
            ? `bg-white body-container`
            : `bg-blue body-container`
        }
      >
        <Outlet />
      </Container>
      <Container bgClass="bg-header-footer bg-footer-content footer-container">
        <Footer />
      </Container>
    </main>
  );
};

export default AppLayout;
